import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack } from '@mui/material';
import { getSystemBillingOverview } from '../api/billingPortalApi';
import { getSubscriptionChangeRequests } from '../api/subscriptionChangesApi';
import { getRolePermissionCleanupPlans } from '../api/permissionCleanupApi';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/Button';

export function BillingDashboardPage() {
  const { t } = useTranslation(['billing', 'common', 'pricing', 'subscriptionChanges', 'permissionCleanup', 'navigation']);
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [changeStats, setChangeStats] = useState({
    pendingUpgrades: 0,
    scheduledDowngrades: 0,
    dueChanges: 0,
    failedChanges: 0,
  });
  const [cleanupStats, setCleanupStats] = useState({ scheduled: 0, ready: 0, warnings: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const emptyValue = t('common:empty_value');

  useEffect(() => {
    Promise.all([
      getSystemBillingOverview(),
      getSubscriptionChangeRequests({ per_page: 200 }),
      getRolePermissionCleanupPlans({ per_page: 200 }),
    ])
      .then(([overviewData, changesResponse, cleanupResponse]) => {
        setOverview(overviewData);
        const changes = changesResponse.data || [];
        const cleanupPlans = cleanupResponse.data || [];
        const now = Date.now();
        setChangeStats({
          pendingUpgrades: changes.filter((c) => c.status === 'pending_payment' && c.change_type === 'upgrade').length,
          scheduledDowngrades: changes.filter((c) => c.status === 'scheduled').length,
          dueChanges: changes.filter((c) => c.status === 'scheduled' && c.effective_at && new Date(c.effective_at).getTime() <= now).length,
          failedChanges: changes.filter((c) => c.status === 'failed').length,
        });
        setCleanupStats({
          scheduled: cleanupPlans.filter((p) => p.status === 'scheduled').length,
          ready: cleanupPlans.filter((p) => p.status === 'ready').length,
          warnings: cleanupPlans.filter((p) => (p.warnings || []).length > 0).length,
        });
      })
      .catch(() => setError(t('billing:load_overview_failed')))
      .finally(() => setLoading(false));
  }, [t]);

  const metricCards = useMemo(() => {
    if (!overview) {
      return [];
    }

    return [
      { label: t('billing:open_invoices'), value: overview.open_invoices },
      { label: t('billing:overdue_invoices'), value: overview.overdue_invoices },
      { label: t('billing:paid_this_month'), value: `${overview.paid_this_month} EUR` },
      { label: t('billing:amount_due'), value: `${overview.amount_due} EUR` },
      { label: t('billing:active_subscriptions'), value: overview.active_subscriptions },
      { label: t('billing:payment_blocked_tenants'), value: overview.payment_blocked_tenants ?? 0 },
      { label: t('billing:paid_through_subscriptions'), value: overview.paid_through_subscriptions ?? 0 },
      { label: t('billing:in_grace_subscriptions'), value: overview.in_grace_subscriptions ?? 0 },
      { label: t('billing:manual_payments_this_month'), value: `${overview.manual_payments_this_month ?? 0} EUR` },
      { label: t('billing:stripe_payments_this_month'), value: `${overview.stripe_payments_this_month ?? 0} EUR` },
      { label: t('subscriptionChanges:pendingUpgrades'), value: changeStats.pendingUpgrades },
      { label: t('subscriptionChanges:scheduledDowngrades'), value: changeStats.scheduledDowngrades },
      { label: t('subscriptionChanges:dueChanges'), value: changeStats.dueChanges },
      { label: t('subscriptionChanges:failedChanges'), value: changeStats.failedChanges },
      { label: t('permissionCleanup:scheduledPlans'), value: cleanupStats.scheduled },
      { label: t('permissionCleanup:readyPlans'), value: cleanupStats.ready },
      { label: t('permissionCleanup:plansWithWarnings'), value: cleanupStats.warnings },
    ];
  }, [overview, changeStats, cleanupStats, t]);

  const invoiceColumns = useMemo(
    () => [
      {
        id: 'number',
        label: t('billing:number'),
        render: (invoice) => invoice.public_number || emptyValue,
      },
      {
        id: 'customer',
        label: t('billing:customer'),
        render: (invoice) => invoice.customer?.name || invoice.customer_id || emptyValue,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (invoice) => (
          invoice.status
            ? <StatusBadge status={invoice.status} category="invoice" size="Sm" />
            : emptyValue
        ),
      },
      {
        id: 'total',
        label: t('billing:total'),
        render: (invoice) => `${invoice.total_amount} ${invoice.currency}`,
      },
      {
        id: 'due',
        label: t('billing:due'),
        render: (invoice) => (
          invoice.due_at
            ? new Date(invoice.due_at).toLocaleDateString()
            : emptyValue
        ),
      },
    ],
    [emptyValue, t],
  );

  const paymentColumns = useMemo(
    () => [
      {
        id: 'id',
        label: t('common:id'),
        render: (payment) => payment.id ?? emptyValue,
      },
      {
        id: 'customer',
        label: t('billing:customer'),
        render: (payment) => payment.customer?.name || payment.customer_id || emptyValue,
      },
      {
        id: 'amount',
        label: t('billing:amount'),
        render: (payment) => `${payment.amount} ${payment.currency}`,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (payment) => (
          payment.status
            ? <StatusBadge status={payment.status} category="payment" size="Sm" />
            : emptyValue
        ),
      },
    ],
    [emptyValue, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('billing:dashboard_title')}
        subtitle={t('billing:dashboard_subtitle')}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button variant="Secondary" component={RouterLink} to="/billing/invoices">
              {t('billing:invoices.title')}
            </Button>
            <Button variant="Secondary" component={RouterLink} to="/billing/module-prices">
              {t('pricing:module_prices.title')}
            </Button>
            <Button variant="Secondary" component={RouterLink} to="/billing/pricing-preview">
              {t('pricing:pricing_preview.title')}
            </Button>
            <Button variant="Secondary" component={RouterLink} to="/billing/sms-usage">
              {t('pricing:sms_usage.title')}
            </Button>
            <Button variant="Secondary" component={RouterLink} to="/billing/permission-cleanup">
              {t('permissionCleanup:title')}
            </Button>
          </Stack>
        )}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2.5}>
        {loading ? (
          <>
            <SectionCard
              variant="Default"
              title={t('navigation:billing_overview')}
              sx={{ mb: 0 }}
            >
              <LoadingSkeleton type="CardGrid" rows={6} />
            </SectionCard>
            <SectionCard variant="Default" title={t('billing:recent_invoices')} noPadding sx={{ mb: 0 }}>
              <LoadingSkeleton type="Table" embedded rows={3} columns={5} />
            </SectionCard>
            <SectionCard variant="Default" title={t('billing:recent_payments')} noPadding sx={{ mb: 0 }}>
              <LoadingSkeleton type="Table" embedded rows={3} columns={4} />
            </SectionCard>
          </>
        ) : overview ? (
          <>
            <SectionCard
              variant="Default"
              title={t('navigation:billing_overview')}
              sx={{ mb: 0 }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 2,
                  width: '100%',
                }}
              >
                {metricCards.map((card) => (
                  <MetricCard
                    key={card.label}
                    value={card.value}
                    label={card.label}
                  />
                ))}
              </Box>
            </SectionCard>

            <SectionCard
              variant="Default"
              title={t('billing:recent_invoices')}
              noPadding
              sx={{ mb: 0 }}
            >
              <DataTable
                type="Default"
                embedded
                columns={invoiceColumns}
                rows={overview.recent_invoices || []}
                getRowKey={(invoice) => invoice.id}
                emptyMessage={t('billing:no_invoices')}
                onRowClick={(invoice) => {
                  if (invoice.id) {
                    navigate(`/billing/invoices/${invoice.id}`);
                  }
                }}
                sx={{
                  overflowX: 'auto',
                  '& table': { minWidth: '100%' },
                }}
              />
            </SectionCard>

            <SectionCard
              variant="Default"
              title={t('billing:recent_payments')}
              noPadding
              sx={{ mb: 0 }}
            >
              <DataTable
                type="Default"
                embedded
                columns={paymentColumns}
                rows={overview.recent_payments || []}
                getRowKey={(payment) => payment.id}
                emptyMessage={t('common:no_data_found')}
                sx={{
                  overflowX: 'auto',
                  '& table': { minWidth: '100%' },
                }}
              />
            </SectionCard>
          </>
        ) : null}
      </Stack>
    </Box>
  );
}
